// GLSL for the "motion pro" passes. Everything here is a pure function of its uniforms (time arrives as a uniform),
// so a frame is reproducible: same project + same time -> same pixels.

export const FULLSCREEN_VS = `#version 300 es
out vec2 vUV;
void main(){ vec2 p = vec2((gl_VertexID << 1) & 2, gl_VertexID & 2); vUV = p; gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0); }`;

const NOISE = `
uint ihash(uint x){ x ^= x >> 16; x *= 0x7feb352du; x ^= x >> 15; x *= 0x846ca68bu; x ^= x >> 16; return x; }
float h01(uint x){ return float(ihash(x) >> 8) * (1.0 / 16777216.0); }
float hash21(vec2 p){ return h01(uint(int(p.x) * 73856093 ^ int(p.y) * 19349663 + 83492791)); }
float vnoise(vec2 p){
  vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash21(i), hash21(i + vec2(1,0)), f.x), mix(hash21(i + vec2(0,1)), hash21(i + vec2(1,1)), f.x), f.y);
}
float fbm(vec2 p){ float a = 0.5, s = 0.0; for (int i = 0; i < 4; i++) { s += a * vnoise(p); p = p * 2.03 + 11.7; a *= 0.5; } return s; }
`;

/** Animated procedural backdrops, alpha-blended over the scene colour. uKind: 1 aurora, 2 radial, 3 grid, 4 dots, 5 noise. */
export const BACKDROP_FS = `#version 300 es
precision highp float; precision highp int;
in vec2 vUV; out vec4 outColor;
uniform int uKind; uniform float uTime; uniform float uAspect; uniform vec3 uA; uniform vec3 uB; uniform vec3 uC;
uniform float uIntensity; uniform float uSpeed; uniform float uScale; uniform vec2 uRes;
${NOISE}
void main(){
  vec2 uv = vUV; vec2 p = (uv - 0.5) * vec2(uAspect, 1.0); float t = uTime * uSpeed;
  vec3 col = vec3(0.0); float a = 0.0;
  if (uKind == 1) {                                   // aurora: three soft, drifting, domain-warped light pools (additive, never hard-edged)
    vec2 q = p * (1.5 / uScale);
    q += 0.30 * vec2(sin(q.y * 1.9 + t * 0.5), cos(q.x * 1.6 - t * 0.4));
    q += 0.22 * vec2(fbm(q * 1.2 + t * 0.10), fbm(q * 1.2 - t * 0.08 + 5.0)) - 0.11;
    vec2 c0 = 0.75 * vec2(sin(t * 0.31), cos(t * 0.23));
    vec2 c1 = 0.85 * vec2(cos(t * 0.27 + 2.1), sin(t * 0.35 + 1.0));
    vec2 c2 = 0.85 * vec2(sin(t * 0.22 + 4.0), cos(t * 0.29 + 0.5));
    float w0 = exp(-dot(q - c0, q - c0) * 0.85), w1 = exp(-dot(q - c1, q - c1) * 1.0), w2 = exp(-dot(q - c2, q - c2) * 1.1);
    float haze = 0.85 + 0.3 * fbm(q * 2.0 + t * 0.05);
    vec3 sum = (uA * w0 + uB * w1 + uC * w2) * haze * uIntensity * 0.42;
    outColor = vec4(sum, clamp((w0 + w1 + w2) * 0.5 * uIntensity, 0.0, 1.0)); return;
  } else if (uKind == 2) {                            // radial glow with a slow pulse
    float d = length(p) / uScale;
    float pulse = 0.92 + 0.08 * sin(t * 1.3);
    float g = exp(-d * d * 2.4) * pulse;
    col = mix(uB, uA, smoothstep(0.0, 0.8, g)); a = clamp(g * uIntensity, 0.0, 1.0);
    a = max(a, 0.0);
  } else if (uKind == 3) {                            // grid: scrolling lines that fade toward the edges
    float cell = 90.0 * uScale / uRes.y;
    vec2 g = p / cell + vec2(0.0, t * 0.6);
    vec2 f = abs(fract(g) - 0.5);
    float edge = min(0.5 - f.x, 0.5 - f.y);
    float line = 1.0 - smoothstep(0.0, 0.03, edge);
    float fade = 1.0 - smoothstep(0.25, 1.0, length(p));
    col = mix(uA, uB, clamp(uv.y, 0.0, 1.0)); a = line * fade * 0.55 * uIntensity;
  } else if (uKind == 4) {                            // dot matrix with a travelling wave
    float cell = 56.0 * uScale / uRes.y;
    vec2 g = p / cell; vec2 id = floor(g); vec2 f = fract(g) - 0.5;
    float wave = 0.5 + 0.5 * sin(length(id) * 0.35 - t * 1.6);
    float r = mix(0.06, 0.26, wave);
    float dd = 1.0 - smoothstep(r - 0.04, r + 0.04, length(f));
    float fade = 1.0 - smoothstep(0.3, 1.0, length(p));
    col = mix(uA, uB, wave); a = dd * fade * (0.35 + 0.5 * wave) * uIntensity;
  } else if (uKind == 5) {                            // flowing fbm clouds
    vec2 q = p * (2.2 / uScale);
    float n = fbm(q + vec2(t * 0.15, -t * 0.1) + fbm(q * 1.3 - t * 0.08) * 1.4);
    col = mix(mix(uA, uB, smoothstep(0.25, 0.6, n)), uC, smoothstep(0.55, 0.85, n));
    a = smoothstep(0.2, 0.8, n) * 0.9 * uIntensity;
  }
  a = clamp(a, 0.0, 1.0);
  outColor = vec4(col * a, a);                        // premultiplied
}`;

/** Instanced particle quads. Each particle is a pure function of (seed, instance, cycle, time). uKind: 0 dots, 1 sparks, 2 bokeh, 3 snow, 4 embers, 5 stars. */
export const PARTICLE_VS = `#version 300 es
precision highp float; precision highp int;
uniform mat4 uViewProj; uniform vec3 uCenter; uniform vec3 uRight; uniform vec3 uUp;
uniform float uTime; uniform vec2 uArea; uniform int uKind; uniform uint uSeed;
uniform float uSize; uniform float uSizeVar; uniform float uSpeed; uniform float uGravity; uniform float uDrift; uniform float uLife;
uniform float uOpacity; uniform float uScale; uniform vec3 uColA; uniform vec3 uColB;
out vec2 vUV; out vec3 vColor; out float vAlpha;
${NOISE}
void main(){
  uint id = uint(gl_InstanceID);
  float ph0 = h01(id * 977u + uSeed * 7919u + 3u);
  float cyc = floor(uTime / uLife + ph0);
  float ph = fract(uTime / uLife + ph0);
  float age = ph * uLife;
  uint b = id * 131u + uSeed * 104729u + uint(max(cyc, 0.0)) * 15485863u + 11u;
  float h1 = h01(b), h2 = h01(b + 1u), h3 = h01(b + 2u), h4 = h01(b + 3u), h5 = h01(b + 4u), h6 = h01(b + 5u);
  vec2 pos = (vec2(h1, h2) - 0.5) * uArea;
  float size = uSize * (1.0 - uSizeVar * h3);
  float env = smoothstep(0.0, 0.14, ph) * smoothstep(1.0, 0.72, ph);
  vec2 vel = vec2(0.0); float alpha = env; vec2 st = vec2(1.0);
  vec2 sway = vec2(sin(age * 1.3 + h6 * 6.2832), cos(age * 0.9 + h5 * 6.2832)) * uDrift * 0.5;
  float damp = 1.0;
  if (uKind == 0) { float ang = h4 * 6.2832; vel = vec2(cos(ang), sin(ang)) * uSpeed * (0.3 + 0.7 * h5); alpha = env * (0.45 + 0.55 * h6); }
  else if (uKind == 1) { float ang = 1.5708 + (h4 - 0.5) * 2.2; vel = vec2(cos(ang), sin(ang)) * uSpeed * (0.6 + h5) * 3.0; alpha = env * (0.6 + 0.4 * h6); damp = 1.0 - 0.45 * ph; sway *= 0.2; size *= 0.55; }
  else if (uKind == 2) { float ang = h4 * 6.2832; vel = vec2(cos(ang), sin(ang)) * uSpeed * 0.4 * h5; size = uSize * (1.6 + 2.4 * h3); alpha = env * 0.32 * (0.5 + h6); }
  else if (uKind == 3) { vel = vec2(0.0, -uSpeed * (0.5 + 0.7 * h5)); sway *= 1.6; size *= (0.45 + 0.55 * h3); alpha = env * (0.55 + 0.45 * h6); }
  else if (uKind == 4) { vel = vec2(0.0, uSpeed * (0.35 + 0.65 * h5)); alpha = env * (0.55 + 0.45 * sin(age * 11.0 + h6 * 40.0) * 0.5 + 0.2); size *= (1.0 - 0.6 * ph); }
  else { vel = vec2(0.0); sway = vec2(0.0); float tw = 0.5 + 0.5 * sin(uTime * (0.8 + 3.2 * h5) + h6 * 6.2832); alpha = 0.18 + 0.82 * tw * tw; size *= (0.4 + 0.6 * h3); age = 0.0; }
  vec2 p = pos + vel * age * damp + 0.5 * vec2(0.0, -uGravity) * age * age + sway;
  vec2 dir = normalize(vel + vec2(0.0, -uGravity) * age + vec2(1e-4));
  vec2 corner = vec2(float(gl_VertexID & 1) * 2.0 - 1.0, float(gl_VertexID >> 1) * 2.0 - 1.0);
  vec2 axisX = vec2(1.0, 0.0), axisY = vec2(0.0, 1.0);
  vec2 hs = vec2(size * 0.5);
  if (uKind == 1) { axisX = dir; axisY = vec2(-dir.y, dir.x); hs = vec2(size * (1.0 + length(vel) * 0.012), size * 0.35); }
  vec2 local = axisX * corner.x * hs.x + axisY * corner.y * hs.y;
  vec3 world = uCenter + vec3(p, 0.0) * uScale + (uRight * local.x + uUp * local.y) * uScale;
  gl_Position = uViewProj * vec4(world, 1.0);
  vUV = corner; vColor = mix(uColA, uColB, h4); vAlpha = alpha * uOpacity;
}`;
export const PARTICLE_FS = `#version 300 es
precision highp float; precision highp int;
in vec2 vUV; in vec3 vColor; in float vAlpha; out vec4 outColor; uniform float uGlow; uniform int uKind;
void main(){
  float d = length(vUV);
  float hard = 1.0 - smoothstep(0.82, 1.0, d);
  float soft = pow(max(1.0 - d, 0.0), 2.2);
  float cross = uKind == 5 ? max(0.0, 1.0 - min(abs(vUV.x), abs(vUV.y)) * 9.0) * max(0.0, 1.0 - d) : 0.0;
  float a = max(mix(hard, soft, uGlow), cross) * vAlpha;
  outColor = vec4(vColor * a, a);                     // premultiplied (additive blending ignores alpha)
}`;

/** Scene transitions: composite incoming layer B over the accumulated frame A. uE = eased progress, uU = linear progress. */
export const TRANSITION_FS = `#version 300 es
precision highp float; precision highp int;
in vec2 vUV; out vec4 outColor;
uniform sampler2D uA; uniform sampler2D uB; uniform float uE; uniform float uU; uniform int uKind; uniform float uAspect;
${NOISE}
vec4 sA(vec2 uv){ return texture(uA, clamp(uv, 0.0, 1.0)); }
vec4 sB(vec2 uv){ return texture(uB, clamp(uv, 0.0, 1.0)); }
vec2 zoom(vec2 uv, float s){ return (uv - 0.5) / s + 0.5; }
vec4 blurA(vec2 uv, float r){ vec4 c = sA(uv) * 0.2; for (int i = 0; i < 8; i++) { float a = float(i) * 0.7854; c += sA(uv + vec2(cos(a), sin(a)) * r) * 0.1; } return c; }
vec4 blurB(vec2 uv, float r){ vec4 c = sB(uv) * 0.2; for (int i = 0; i < 8; i++) { float a = float(i) * 0.7854; c += sB(uv + vec2(cos(a), sin(a)) * r) * 0.1; } return c; }
void main(){
  vec2 uv = vUV; float e = uE; float soft = 0.05; vec4 c;
  if (uKind == 1) c = mix(sA(uv), sB(uv), e);                                                       // fade
  else if (uKind == 2) c = (uv.x >= 1.0 - e) ? sB(uv - vec2(1.0 - e, 0.0)) : sA(uv);                  // slide_left  (B enters from the right)
  else if (uKind == 3) c = (uv.x <= e) ? sB(uv + vec2(1.0 - e, 0.0)) : sA(uv);                        // slide_right (from the left)
  else if (uKind == 4) c = (uv.y <= e) ? sB(uv + vec2(0.0, 1.0 - e)) : sA(uv);                        // slide_up    (from the bottom)
  else if (uKind == 5) c = (uv.y >= 1.0 - e) ? sB(uv - vec2(0.0, 1.0 - e)) : sA(uv);                  // slide_down  (from the top)
  else if (uKind == 6) c = (uv.x >= 1.0 - e) ? sB(uv - vec2(1.0 - e, 0.0)) : sA(uv + vec2(e, 0.0));   // push_left
  else if (uKind == 7) c = (uv.y <= e) ? sB(uv + vec2(0.0, 1.0 - e)) : sA(uv - vec2(0.0, e));         // push_up
  else if (uKind == 8) c = mix(sA(zoom(uv, mix(1.0, 1.5, e))), sB(uv), smoothstep(0.15, 0.85, e));    // zoom_in : A rushes past
  else if (uKind == 9) c = mix(sA(uv), sB(zoom(uv, mix(1.5, 1.0, e))), smoothstep(0.15, 0.85, e));    // zoom_out: B settles in
  else if (uKind == 10) { float m = smoothstep(1.0 - e - soft, 1.0 - e + 0.0001, uv.x); c = mix(sA(uv), sB(uv), e >= 1.0 ? 1.0 : m * step(0.0001, e)); }   // wipe_left
  else if (uKind == 11) { float m = 1.0 - smoothstep(e - 0.0001, e + soft, uv.y); c = mix(sA(uv), sB(uv), e >= 1.0 ? 1.0 : m * step(0.0001, e)); }       // wipe_up
  else if (uKind == 12) { float r = e * (0.5 * sqrt(uAspect * uAspect + 1.0) + 0.06); float d = length((uv - 0.5) * vec2(uAspect, 1.0));
                          c = mix(sA(uv), sB(uv), 1.0 - smoothstep(r - 0.03, r, d)); }                 // iris
  else if (uKind == 13) { float r = sin(3.14159 * e) * 0.035; c = mix(blurA(uv, r), blurB(uv, r), smoothstep(0.3, 0.7, e)); }   // blur
  else if (uKind == 14) {                                                                            // glitch
    float band = floor(uv.y * 26.0); float tick = floor(uU * 22.0);
    float hq = h01(uint(band * 7.0 + tick * 131.0 + 5.0)); float amp = sin(3.14159 * e);
    float sh = (hq - 0.5) * 0.16 * amp * step(0.45, h01(uint(band * 3.0 + tick * 17.0 + 9.0)) + e * 0.4);
    float ca = 0.012 * amp; bool showB = hq < e;
    vec4 r = showB ? sB(uv + vec2(sh + ca, 0.0)) : sA(uv + vec2(sh + ca, 0.0));
    vec4 g = showB ? sB(uv + vec2(sh, 0.0)) : sA(uv + vec2(sh, 0.0));
    vec4 b = showB ? sB(uv + vec2(sh - ca, 0.0)) : sA(uv + vec2(sh - ca, 0.0));
    c = vec4(r.r, g.g, b.b, 1.0);
  } else c = sB(uv);
  outColor = vec4(c.rgb, 1.0);
}`;

export const COPY_FS = `#version 300 es
precision highp float; precision highp int; in vec2 vUV; out vec4 outColor; uniform sampler2D uTex;
void main(){ outColor = texture(uTex, vUV); }`;

export const BRIGHT_FS = `#version 300 es
precision highp float; precision highp int; in vec2 vUV; out vec4 outColor; uniform sampler2D uTex; uniform float uThreshold;
void main(){
  vec3 c = texture(uTex, vUV).rgb; float l = max(max(c.r, c.g), c.b);
  float k = smoothstep(uThreshold, min(1.0, uThreshold + 0.3), l);
  outColor = vec4(c * k, 1.0);
}`;

export const BLUR_FS = `#version 300 es
precision highp float; precision highp int; in vec2 vUV; out vec4 outColor; uniform sampler2D uTex; uniform vec2 uStep;
void main(){
  vec3 c = texture(uTex, vUV).rgb * 0.227027;
  c += (texture(uTex, vUV + uStep * 1.3846).rgb + texture(uTex, vUV - uStep * 1.3846).rgb) * 0.3162162;
  c += (texture(uTex, vUV + uStep * 3.2308).rgb + texture(uTex, vUV - uStep * 3.2308).rgb) * 0.0702703;
  outColor = vec4(c, 1.0);
}`;

/** Final grade + effects written to the screen: chromatic aberration, bloom add, exposure/contrast/saturation, vignette, grain. */
export const POST_FS = `#version 300 es
precision highp float; precision highp int;
in vec2 vUV; out vec4 outColor;
uniform sampler2D uBase; uniform sampler2D uB0; uniform sampler2D uB1; uniform sampler2D uB2;
uniform float uBloom; uniform float uVig; uniform float uGrain; uniform float uAber; uniform float uExp; uniform float uCon; uniform float uSat;
uniform float uSeed; uniform float uAspect; uniform int uLevels;
${NOISE}
void main(){
  vec2 uv = vUV, c = uv - 0.5;
  vec2 off = c * (uAber * 0.02) * (dot(c, c) * 4.0 + 0.15);
  vec3 col = vec3(texture(uBase, uv + off).r, texture(uBase, uv).g, texture(uBase, uv - off).b);
  if (uBloom > 0.0) {
    vec3 bl = texture(uB0, uv).rgb * 0.5 + texture(uB1, uv).rgb * 0.6;
    if (uLevels > 2) bl += texture(uB2, uv).rgb * 0.7;
    col += bl * uBloom;
  }
  col *= uExp;
  col = (col - 0.5) * uCon + 0.5;
  float lum = dot(col, vec3(0.2126, 0.7152, 0.0722)); col = mix(vec3(lum), col, uSat);
  float v = smoothstep(0.35, 1.0, length(c) * 1.4142);
  col *= 1.0 - uVig * v * 0.9;
  float n = h01(uint(gl_FragCoord.x) * 1973u + uint(gl_FragCoord.y) * 9277u + uint(uSeed) * 26699u) - 0.5;
  col += n * uGrain * 0.16;
  outColor = vec4(clamp(col, 0.0, 1.0), 1.0);
}`;
